// Every user-facing string on the public appointment-confirmation page.
//
// The page is Hebrew/RTL for everyone except a patient the clinic explicitly
// marked `communicationLanguage: "en"` (and only while the clinic has
// `englishCommunicationEnabled` on). The server resolves that and returns it as
// `link.language`; anything other than "en" renders the Hebrew bag, so a link
// created before this feature existed behaves exactly as it always did.
//
// The server never sends display text to this page — its error responses are
// codes, mapped to strings here — so translations live entirely client-side.

export const CONFIRM_STRINGS = {
  he: {
    // Loading / error stages
    loading: "טוען פרטי הפגישה...",
    saving: "שומר את התשובה שלך...",
    errorTitle: "אופס!",
    linkNotFound: "הקישור לא נמצא או שפג תוקפו",
    linkInactive: "הקישור אינו פעיל יותר",
    loadError: "אירעה שגיאה בטעינת הקישור",
    appointmentNotFound: "הפגישה לא נמצאה במערכת",
    appointmentUpdateFailed: "אירעה שגיאה בעדכון הפגישה",
    saveResponseError: "אירעה שגיאה בשמירת התשובה",

    // Clinic header context tag
    pageEyebrow: "אישור הגעה לפגישה",

    // Verification stage
    verifyTitle: "אימות זהות",
    verifySubtitle: "להמשך, הזן/י את 4 הספרות האחרונות של מספר הטלפון שלך",
    verifyInputLabel: "4 הספרות האחרונות של מספר הטלפון",
    verifyContinue: "המשך",
    enterFourDigits: "יש להזין 4 ספרות",
    invalidCode: "הקוד שהוזן שגוי",
    tooManyAttempts:
      "יותר מדי ניסיונות. נסה/י שוב מאוחר יותר או פנה/י למטפל/ת.",
    linkInactiveVerify: "הקישור אינו פעיל עוד. פנה/י למטפל/ת לקבלת קישור חדש.",
    verifyGenericError: "אירעה שגיאה באימות",

    // Details stage
    greetingHello: "שלום",
    greetingSub: "נקבעה לך פגישה. האם תוכל/י לאשר את הגעתך?",
    detailsTitle: "פרטי הפגישה",
    labelDate: "תאריך",
    labelTime: "שעה",
    labelDuration: "משך",
    durationUnit: "דקות",
    labelVideoMeeting: "פגישת וידאו אונליין",
    videoMeetingHint: "ההצטרפות דרך הקישור, כשעה לפני מועד הפגישה",
    videoJoin: "הצטרפות",
    videoJoinAria: "הצטרפות לשיחת הווידאו",
    labelAddress: "כתובת",
    labelPhone: "טלפון",
    navigate: "נווט",
    navigateAria: "נווט עם Waze",
    confirmAttendance: "אשר הגעה",
    cannotAttend: "לא אוכל להגיע",
    blockedTitle: "לא ניתן לבטל את הפגישה דרך הקישור.",
    blockedSub: "אנא צור/י קשר עם הקליניקה לביטול הפגישה.",
    callClinic: "התקשר לקליניקה",

    // Success stage
    successConfirmedTitle: "הפגישה אושרה בהצלחה!",
    successDeclinedTitle: "הפגישה בוטלה",
    successConfirmedMessage: "תודה על האישור! נתראה בפגישה.",
    successDeclinedMessage:
      "הפגישה בוטלה. אנא צור/י קשר עם הקליניקה לקביעת מועד חדש.",
    addToCalendar: "הוסף ליומן",
    navigateWithWaze: "נווט עם Waze",
    changeResponse: "שנה תשובה",

    // Decline popup
    popupConfirmTitle: "האם לאשר את הפגישה?",
    popupDeclineTitle: "האם לבטל את הפגישה?",
    popupConfirmText: "אנחנו שמחים לשמוע! נתראה בפגישה.",
    popupDeclineText: "שים/י לב: ביטול הפגישה ידרוש תיאום מחדש עם הקליניקה.",
    popupCancel: "ביטול",
    popupConfirmAction: "אשר",
    popupDeclineAction: "בטל פגישה",

    // Calendar event text
    calendarTitle: (clinicName) => `פגישה ב${clinicName}`,
    calendarDetails: (clinicName) => `פגישה עם ${clinicName}`,
    calendarFallbackClinic: "קליניקה",
    calendarFallbackClinicWith: "הקליניקה",
  },

  en: {
    loading: "Loading appointment details...",
    saving: "Saving your response...",
    errorTitle: "Oops!",
    linkNotFound: "This link was not found or has expired",
    linkInactive: "This link is no longer active",
    loadError: "Something went wrong while loading the link",
    appointmentNotFound: "The appointment was not found",
    appointmentUpdateFailed: "Something went wrong while updating the appointment",
    saveResponseError: "Something went wrong while saving your response",

    pageEyebrow: "Appointment confirmation",

    verifyTitle: "Identity verification",
    verifySubtitle: "To continue, enter the last 4 digits of your phone number",
    verifyInputLabel: "Last 4 digits of your phone number",
    verifyContinue: "Continue",
    enterFourDigits: "Please enter 4 digits",
    invalidCode: "That code is incorrect",
    tooManyAttempts:
      "Too many attempts. Please try again later or contact your therapist.",
    linkInactiveVerify:
      "This link is no longer active. Please contact your therapist for a new one.",
    verifyGenericError: "Something went wrong during verification",

    greetingHello: "Hello",
    greetingSub: "You have an appointment scheduled. Can you confirm you'll attend?",
    detailsTitle: "Appointment details",
    labelDate: "Date",
    labelTime: "Time",
    labelDuration: "Duration",
    durationUnit: "minutes",
    labelVideoMeeting: "Online video appointment",
    videoMeetingHint: "Join through the link, from about an hour before the appointment",
    videoJoin: "Join",
    videoJoinAria: "Join the video call",
    labelAddress: "Address",
    labelPhone: "Phone",
    navigate: "Navigate",
    navigateAria: "Navigate with Waze",
    confirmAttendance: "Confirm attendance",
    cannotAttend: "I can't make it",
    blockedTitle: "This appointment can no longer be cancelled through the link.",
    blockedSub: "Please contact the clinic to cancel.",
    callClinic: "Call the clinic",

    successConfirmedTitle: "Your appointment is confirmed!",
    successDeclinedTitle: "Appointment cancelled",
    successConfirmedMessage: "Thank you for confirming. See you at the appointment.",
    successDeclinedMessage:
      "The appointment has been cancelled. Please contact the clinic to book a new time.",
    addToCalendar: "Add to calendar",
    navigateWithWaze: "Navigate with Waze",
    changeResponse: "Change response",

    popupConfirmTitle: "Confirm this appointment?",
    popupDeclineTitle: "Cancel this appointment?",
    popupConfirmText: "Great to hear. See you at the appointment.",
    popupDeclineText:
      "Please note: cancelling means the appointment will have to be rescheduled with the clinic.",
    popupCancel: "Back",
    popupConfirmAction: "Confirm",
    popupDeclineAction: "Cancel appointment",

    calendarTitle: (clinicName) => `Appointment at ${clinicName}`,
    calendarDetails: (clinicName) => `Appointment with ${clinicName}`,
    calendarFallbackClinic: "the clinic",
    calendarFallbackClinicWith: "the clinic",
  },
};

/** String bag for a language. Anything but "en" is Hebrew. */
export const getConfirmStrings = (language) =>
  CONFIRM_STRINGS[language === "en" ? "en" : "he"];

/** Date/time locale matching the page language. en-GB is day-first, as in Israel. */
export const getConfirmLocale = (language) =>
  language === "en" ? "en-GB" : "he-IL";
